'use client';

import { CustomFieldProps, useField } from 'informed';
import { InputHTMLAttributes, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomFormBlockFieldProps } from './contracts';
import { InputBlock, InputBlockProps } from './primitives/InputBlock';
import { InputStructureProps } from './structures/InputStructure';
import { getErrorTranslation } from './utils';
import { ValidatorMessage } from '@/helpers/validators';

export function asBlockField<Attr, P extends InputStructureProps & Attr, V>(
  Component: React.ComponentType<P>
): React.ComponentType<CustomFieldProps<CustomFormBlockFieldProps<V>, V> & P> {
  return ({
    transformValue,
    touchOnChange,
    onChange,
    onBlur,
    onFocus,
    translateError = true,
    error: propsError,
    preventValidation,
    ...fieldProps
  }) => {
    const { t } = useTranslation();

    const { fieldState, fieldApi, render, ref, userProps } = useField<P, V>(
      fieldProps
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        fieldApi.setValue(
          transformValue ? transformValue(value) : (value as V),
          e
        );
        touchOnChange && fieldApi.setTouched(true);

        if (onChange) {
          onChange(e);
        }
      },
      [onChange, fieldApi, transformValue, touchOnChange]
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (preventValidation) {
          e.preventDefault();
          return;
        }

        fieldApi.setTouched(true);

        if (onBlur) {
          onBlur(e);
        }
      },
      [onBlur, fieldApi, preventValidation]
    );

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (onFocus) {
          onFocus(e);
        }
      },
      [onFocus]
    );

    const getError = useCallback(() => {
      if (propsError) return propsError;

      if (fieldState.error != null && translateError) {
        return getErrorTranslation(t, fieldState.error as ValidatorMessage);
      }
      return fieldState.error;
    }, [t, propsError, translateError, fieldState.error]);

    return render(
      <Component
        {...userProps}
        {...fieldState}
        {...fieldApi}
        name={fieldProps.name}
        ref={ref}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        value={fieldState.value == null ? '' : fieldState.value}
        error={getError()}
      />
    );
  };
}

export const Input = asBlockField<
  InputHTMLAttributes<HTMLInputElement>,
  InputBlockProps & React.RefAttributes<HTMLInputElement>,
  string
>(InputBlock);
